<template>
  <WdsStack justify-space-between class="tw-mx-1 tw-pb-1.5" direction-column>
    <WdsStack justify-space-between>
      <WdsStack justify-start gap-sm align-center>
        <WdsInput
          type="text"
          class="tw-min-w-[250px]"
          default
          prefix-icon="search"
          :placeholder="$t('Search name, email, job title...')"
          data-test="tc-search-input"
          @update:model-value="(value) => $emit('search', value)"
        />
        <WdsButtonGhost
          class="tw-h-7.5 !tw-mr-2"
          :class="{ 'filters-active': filtersAreOpened }"
          data-test="tc-filter-button"
          :disabled="filtersAreDisabled"
          @click="toggleFilters"
        >
          <div class="tw-my-auto tw-px-3">
            <WdsStack gap-sm align-center>
              <WdsIcon name="settings-adjust" :size="16" />
              {{ $t('Filters') }}
              <WdsBadge v-if="appliedFiltersCount > 0" lg primary>
                {{ appliedFiltersCount }}
              </WdsBadge>
            </WdsStack>
          </div>
        </WdsButtonGhost>
      </WdsStack>
      <WdsStack align-center gap-sm justify-center>
        <WdsSelect
          v-model="filters.statuses"
          can-deselect
          multi-select
          :list="statusesOptions"
          :placeholder="$t('Status')"
          class="tw-w-[150px]"
          data-test="tc-filter-status"
          max-labels-visible="1"
          labels-primary
        >
          <template #option="{ option }">
            <div :data-test="`filter-status-${option.label.replace(' ', '-').toLowerCase()}`">
              {{ option.label }}
            </div>
          </template>
        </WdsSelect>
        <WdsSelect
          v-model="filters.countries"
          multi-select
          can-deselect
          searchable
          :list="filterOptions.countries"
          class="tw-w-[150px]"
          :placeholder="$t('All locations')"
          data-test="tc-filter-country"
          max-labels-visible="1"
          labels-primary
        >
          <template #option="{ option }">
            <div
              class="tw-flex tw-items-center tw-gap-2"
              :data-test="`filter-country-${option.value.toLowerCase()}`"
            >
              <WdsIcon :size="16" :name="computeFlagIcon(option.value)" />
              {{ option.label }}
            </div>
          </template>
        </WdsSelect>
        <DataTableSort :sort-options="sortOptions" @sort="(value) => $emit('sort', value)" />
      </WdsStack>
    </WdsStack>
    <WdsDrawer
      v-model:show="filtersAreOpened"
      position="right"
      width="350px"
      :title="$t('Filters')"
      closable
      class="!tw-bg-true-gray-50 tw-overflow-y-auto"
    >
      <WdsStack justify-space-between direction-column class="tw-h-full">
        <WdsStack gap-md direction-column>
          <WdsText
            class="tw-border-b-gray-300 tw-border-solid tw-border-b tw-mt-4 tw-pb-1"
            tertiary
            :text="$t('System filters')"
          />
          <WdsSelect
            v-if="displayStatesFilter"
            v-model="filters.states"
            multi-select
            can-deselect
            searchable
            :list="filterOptions.states"
            :label="$t('US States')"
            :placeholder="$t('All')"
            data-test="tc-filter-state"
            labels-primary
          >
            <template #option="{ option }">
              <div
                class="tw-flex tw-items-center tw-gap-2"
                :data-test="`filter-state-${option.value.toLowerCase()}`"
              >
                {{ option.label }}
              </div>
            </template>
          </WdsSelect>
          <WdsSelect
            v-model="filters.skills"
            multi-select
            can-deselect
            searchable
            :disabled="skillsLoading"
            :list="skillsOptions"
            :load-more-distance="20"
            :placeholder="$t('All')"
            data-test="tc-filter-skills"
            labels-primary
            @load-more="loadMoreSkills"
          >
            <template #label>
              <WdsStack class="tw-w-full" justify-space-between align-center>
                <span class="tw-pt-1">{{ $t('Skills') }}</span>
                <span>
                  <WdsToggle
                    :checked="!showWorkerSkills"
                    label-position="left"
                    primary
                    data-test="worker-skills-toggle"
                    @update:checked="toggleShowWorkerSkills"
                  >
                    Only Company added
                  </WdsToggle>
                </span>
              </WdsStack>
            </template>
            <template #option="{ option }">
              <div :data-test="`filter-skill-${option.label.toLowerCase()}`">
                {{ option.label }}
              </div>
            </template>
          </WdsSelect>
          <WdsStack gap-md>
            <div class="tw-w-full">
              <WdsSelect
                v-model="filters.hireStatus"
                can-deselect
                :list="hireStatusOptions"
                :label="$t('Currently hired')"
                :placeholder="$t('All')"
                data-test="tc-filter-hire-status"
              >
                <template #option="{ option }">
                  <div
                    :data-test="`filter-hire-status-${option.label.replace(' ', '-').toLowerCase()}`"
                  >
                    {{ option.label }}
                  </div>
                </template>
              </WdsSelect>
            </div>
            <div class="tw-w-full">
              <WdsSelect
                v-model="filters.hireHistory"
                can-deselect
                :list="hireHistoryOptions"
                :label="$t('Previously Hired')"
                :placeholder="$t('All')"
                data-test="tc-filter-hire-history"
              >
                <template #option="{ option }">
                  <div
                    :data-test="`filter-hire-history-${option.label.replace(' ', '-').toLowerCase()}`"
                  >
                    {{ option.label }}
                  </div>
                </template>
              </WdsSelect>
            </div>
          </WdsStack>
          <WdsSelect
            v-model="filters.businessSetup"
            can-deselect
            multi-select
            searchable
            :list="filterOptions.businessSetup"
            :label="$t('Business setup')"
            :placeholder="$t('All')"
            data-test="tc-filter-business-setup"
            labels-primary
          >
            <template #option="{ option }">
              <div
                :data-test="`filter-business-setup-${option.label.replace(' ', '-').toLowerCase()}`"
              >
                {{ option.label }}
              </div>
            </template>
          </WdsSelect>
          <WdsSelect
            v-if="props.staffingAgencyManagesWorkers"
            v-model="filters.staffingAgencyStatus"
            can-deselect
            :list="staffingAgencyOwnershipOptions"
            :label="$t('Active Staffing Agency ownership')"
            :placeholder="$t('All')"
            data-test="tc-filter-staffing-agency-status"
          >
            <template #option="{ option }">
              <div
                :data-test="`filter-staffing-agency-status-${option.label.replace(' ', '-').toLowerCase()}`"
              >
                {{ option.label }}
              </div>
            </template>
          </WdsSelect>
          <WdsSelect
            v-model="filters.invitedByUsers"
            can-deselect
            multi-select
            searchable
            :list="filterOptions.invitedByUsers"
            :label="$t('Added/Invited by')"
            :placeholder="$t('All')"
            data-test="tc-filter-added-by"
            labels-primary
          >
            <template #option="{ option }">
              <div :data-test="`filter-added-by-${option.label.replace(' ', '-').toLowerCase()}`">
                {{ option.label }}
              </div>
            </template>
          </WdsSelect>
          <WdsSelect
            v-model="filters.origin"
            can-deselect
            :list="originOptions"
            :label="$t('Talent Pool origin')"
            :placeholder="$t('All')"
            data-test="tc-filter-origin"
          >
            <template #label>
              <WdsStack align-center>
                {{ $t('Origin') }}
                <WdsTooltip class="tw-ml-1" bottom global :z-index="50">
                  <WdsIcon name="help-filled" :size="16" />
                  <template #tooltip-content>
                    <p>
                      {{
                        $t('Origin defines the way a Contact was introduced to the Talent Pool.')
                      }}
                    </p>
                    <p>
                      {{
                        $t('Invited: The contact was directly invited through the invitation form')
                      }}
                    </p>
                    <p class="tw-mb-0">
                      {{
                        $t(
                          'Added: The contact either applied and was accepted into the Talent Pool or was automatically added after being hired',
                        )
                      }}
                    </p>
                  </template>
                </WdsTooltip>
              </WdsStack>
            </template>
            <template #option="{ option }">
              <div :data-test="`filter-origin-${option.label.replace(' ', '-').toLowerCase()}`">
                {{ option.label }}
              </div>
            </template>
          </WdsSelect>
          <TrustedContactsCompanyFilter
            v-if="forOrganisation"
            v-model="selectedCompanies"
            :trusted-contacts="filtersResult?.trustedContacts?.data"
          />
          <CustomFieldFilters
            v-model:custom-field-filters="customFieldFilters"
            @gql-filters="customFieldsGqlInput = $event"
          />
        </WdsStack>

        <WdsStack justify-end class="tw-w-full">
          <WdsButton
            class="!tw-mt-4 !tw-mb-2"
            :disabled="!displayResetFiltersButton"
            data-test="tc-filter-reset"
            primary
            @click="resetFilters"
          >
            <div class="tw-my-auto">
              {{ $t('Clear all') }}
            </div>
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </WdsDrawer>
  </WdsStack>
</template>

<script setup lang="ts">
import { ref, watch, computed, reactive, Ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsInput,
  WdsButtonGhost,
  WdsIcon,
  WdsText,
  WdsStack,
  WdsSelect,
  WdsDrawer,
  WdsButton,
  WdsTooltip,
  WdsBadge,
  WdsToggle,
} from '@worksome/wds-components'
import unionBy from 'lodash/unionBy'
import { $t } from '@/composables/translation'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import TrustedContactsCompanyFilter from '@/components/Contacts/CompanyFreelancer/TrustedContactsCompanyFilter.vue'
import {
  OrganisationTrustedContactsFilterData,
  TrustedContactsFilterData,
  OrganisationTrustedContactsSkillsFilterData,
  TrustedContactsSkillsFilterData,
} from '@/components/Contacts/CompanyFreelancer/queries/TrustedContacts'
import { merge } from '@/utils/tools'
import DataTableSort from '@/components/BaseDataTable/DataTableSort.vue'
import {
  SortOrder,
  TrustedContactOrderByColumn,
  TrustedContactOrigin,
  TrustedContactHireHistory,
  TrustedContactHireStatus,
  TrustedContactStaffingAgencyStatus,
  CustomFieldTypeValueInput,
} from '@/gql/graphql'
import { SortOption } from '@/components/BaseDataTable/composables/dataTableSort'
import { ListItem } from '@/components/Workflows/Pages/Workflow/workflow'
import { computeFlagIcon } from '@/utils/ts-tools'
import CustomFieldFilters from '@/components/Fields/CustomFieldFilters.vue'
import { CustomFieldFilter } from '@/components/Fields/custom-fields-types'

const clientId = 'publicApi'

const emit = defineEmits([
  'search',
  'update-filters',
  'sort',
  'filter-drawer-toggled',
  'update:showWorkerSkills',
])
const props = withDefaults(
  defineProps<{
    forOrganisation: boolean
    divisions: any[]
    unfilteredDivisions: any[]
    workerPanelActive: boolean
    staffingAgencyManagesWorkers: boolean
    showWorkerSkills: boolean
  }>(),
  {
    forOrganisation: false,
    divisions: () => [],
    unfilteredDivisions: () => [],
    workerPanelActive: false,
    staffingAgencyManagesWorkers: false,
    showWorkerSkills: false,
  },
)

const filtersQuery = computed(() =>
  !props.forOrganisation ? TrustedContactsFilterData : OrganisationTrustedContactsFilterData,
)

const skillsQuery = computed(() =>
  !props.forOrganisation
    ? TrustedContactsSkillsFilterData
    : OrganisationTrustedContactsSkillsFilterData,
)

const queryVariables = ref({
  first: PER_PAGE_MAXIMUM,
  accounts: [currentAccount.globalId],
})

const skillsVariables = ref({
  page: 1,
  first: PER_PAGE_MAXIMUM,
  accounts: [currentAccount.globalId],
})

const { result: filtersResult } = useQuery(filtersQuery, queryVariables, {
  clientId,
})

const {
  result: skillsResult,
  loading: skillsLoading,
  fetchMore: fetchMoreSkills,
} = useQuery(skillsQuery, skillsVariables, {
  clientId,
})

const loadMoreSkills = () => {
  if (skillsLoading.value) return
  const { hasMorePages, currentPage } = skillsResult?.value?.trustedContacts.paginatorInfo ?? {}
  if (!hasMorePages) return

  fetchMoreSkills({
    variables: {
      page: currentPage + 1,
    },
    updateQuery(prev, { fetchMoreResult }) {
      return merge(prev, fetchMoreResult)
    },
  })
}

const sortByLabel = (itemA, itemB) => itemA.label.trim().localeCompare(itemB.label.trim())

const statusesOptions = [
  { label: $t('Active'), value: 'ACTIVE' },
  { label: $t('Applied'), value: 'APPLIED' },
  { label: $t('Pending invite'), value: 'INVITED' },
  { label: $t('Application declined'), value: 'BLOCKED' },
]

const originOptions = [
  { label: $t('Invited'), value: TrustedContactOrigin.Invited },
  { label: $t('Added'), value: TrustedContactOrigin.Added },
]

const hireHistoryOptions = [
  { label: $t('Yes'), value: TrustedContactHireHistory.PreviouslyHired },
  { label: $t('No'), value: TrustedContactHireHistory.NotPreviouslyHired },
]

const hireStatusOptions = [
  { label: $t('Yes'), value: TrustedContactHireStatus.CurrentlyHired },
  { label: $t('No'), value: TrustedContactHireStatus.NotCurrentlyHired },
]

const staffingAgencyOwnershipOptions = [
  { label: $t('Yes'), value: TrustedContactStaffingAgencyStatus.HasOwnership },
  { label: $t('No'), value: TrustedContactStaffingAgencyStatus.NoOwnership },
]

// Format filter options in the structure expected by the Select component
const filterOptions = computed(() => {
  const options =
    filtersResult.value?.trustedContacts?.data?.reduce(
      (filters, trustedContact) => {
        const { address } = trustedContact.worker
        const { invitedByUser } = trustedContact
        const { primaryBusinessEntity } = trustedContact.worker

        return {
          ...filters,
          countries: address?.country?.name
            ? {
                ...filters.countries,
                [address.country.name]: {
                  label: address.country.name,
                  value: address.country.isoCode,
                },
              }
            : { ...filters.countries },
          states:
            address?.state && address?.country?.isoCode === 'US' && globalStates.US?.[address.state]
              ? {
                  ...filters.states,
                  [address.state]: {
                    label: globalStates.US?.[address.state],
                    value: address.state,
                  },
                }
              : { ...filters.states },
          invitedByUsers: invitedByUser
            ? {
                ...filters.invitedByUsers,
                [invitedByUser.id]: { label: invitedByUser.name, value: invitedByUser.id },
              }
            : { ...filters.invitedByUsers },
          businessSetup:
            primaryBusinessEntity?.localisedName && primaryBusinessEntity?.type
              ? {
                  ...filters.businessSetup,
                  [primaryBusinessEntity.type]: {
                    label: primaryBusinessEntity.localisedName,
                    value: primaryBusinessEntity.type,
                  },
                }
              : { ...filters.businessSetup },
        }
      },
      { countries: {}, states: {}, invitedByUsers: {}, skills: {}, businessSetup: {} },
    ) ?? {}

  const automation = { label: $t('Worksome System'), value: 'Worksome System', isAutomation: true }

  return {
    countries: options.countries ? Object.values(options.countries).sort(sortByLabel) : [],
    states: options.states ? Object.values(options.states).sort(sortByLabel) : [],
    invitedByUsers: options.invitedByUsers
      ? [automation, ...Object.values(options.invitedByUsers).sort(sortByLabel)]
      : [automation],
    businessSetup: options.businessSetup
      ? Object.values(options.businessSetup).sort(sortByLabel)
      : [],
  }
})

const skillsOptions = computed(() => {
  const skills =
    skillsResult.value?.trustedContacts?.data?.reduce((trustedContactAcc, trustedContact) => {
      const combinedSkills = props.showWorkerSkills
        ? unionBy<ListItem>(trustedContact.worker?.skills, trustedContact.skills, 'value')
        : unionBy<ListItem>(trustedContact.skills, [], 'value')

      const trustedContactSkills = combinedSkills.reduce(
        (trustedContactSkillsAcc, { label, value }) => ({
          ...trustedContactSkillsAcc,
          [value]: { label, value },
        }),
        {},
      )

      return {
        ...trustedContactAcc,
        ...trustedContactSkills,
      }
    }, {}) ?? {}

  return skills ? (Object.values(skills).sort(sortByLabel) as ListItem[]) : []
})

const sortOptions: SortOption[] = [
  {
    name: 'name_asc',
    primaryText: $t('Name'),
    secondaryText: $t('A to Z'),
    orderByColumn: TrustedContactOrderByColumn.WorkerName,
    sortOrder: SortOrder.Asc,
  },
  {
    name: 'name_desc',
    primaryText: $t('Name'),
    secondaryText: $t('Z to A'),
    orderByColumn: TrustedContactOrderByColumn.WorkerName,
    sortOrder: SortOrder.Desc,
  },
  {
    name: 'status',
    primaryText: $t('Last status change'),
    secondaryText: null,
    orderByColumn: TrustedContactOrderByColumn.WorkerStatusUpdated,
    sortOrder: SortOrder.Desc,
  },
  {
    name: 'date_added_desc',
    primaryText: $t('Date added'),
    secondaryText: $t('New to old'),
    orderByColumn: TrustedContactOrderByColumn.WorkerDateAdded,
    sortOrder: SortOrder.Desc,
  },
  {
    name: 'date_added_asc',
    primaryText: $t('Date added'),
    secondaryText: $t('Old to new'),
    orderByColumn: TrustedContactOrderByColumn.WorkerDateAdded,
    sortOrder: SortOrder.Asc,
  },
  {
    name: 'jobs_taken_desc',
    primaryText: $t('Jobs Taken'),
    secondaryText: $t('More to less'),
    orderByColumn: TrustedContactOrderByColumn.WorkerJobsCount,
    sortOrder: SortOrder.Desc,
  },
]

const filtersAreOpened = ref(false)
const filtersAreDisabled = ref(false)
const customFieldFilters: CustomFieldFilter[] = reactive([])

interface FiltersInterface {
  statuses: any[]
  origin: any
  skills: any[]
  invitedByUsers: any[]
  countries: any[]
  states: any[]
  accounts: { value: string }[]
  businessSetup: any[]
  hireHistory: ListItem | null
  hireStatus: ListItem | null
  staffingAgencyStatus: ListItem | null
}

const defaultFilters: FiltersInterface = {
  statuses: [],
  origin: null,
  skills: [],
  invitedByUsers: [],
  countries: [],
  states: [],
  accounts: [{ value: currentAccount.globalId }],
  businessSetup: [],
  hireHistory: null,
  hireStatus: null,
  staffingAgencyStatus: null,
}

const filters: FiltersInterface = reactive({ ...defaultFilters })

const customFieldsGqlInput: Ref<CustomFieldTypeValueInput[]> = ref([])

const appliedFiltersCount = computed(() => {
  let appliedFilters = customFieldsGqlInput.value.length

  appliedFilters += Object.entries(filters).reduce((count, [filterName, filterValue]) => {
    const defaultValue = defaultFilters[filterName]

    if (Array.isArray(filterValue)) {
      if (props.forOrganisation && filterName === 'accounts') {
        return filterValue.length > 0 ? count + 1 : count
      }

      return filterValue.length !== defaultValue.length ? count + 1 : count
    }

    return filterValue !== defaultValue && filterValue !== null ? count + 1 : count
  }, 0)

  return appliedFilters
})

watch([filters, customFieldsGqlInput], ([updatedFilters, _]) => {
  const isUSPresent = updatedFilters.countries.some((country) => country.value === 'US')

  const states = !isUSPresent ? [] : updatedFilters.states.map(({ value }) => value)

  // If all are selected, use null as the filter
  const invitedByUsers =
    updatedFilters.invitedByUsers.length === filterOptions.value.invitedByUsers.length
      ? null
      : {
          users: updatedFilters.invitedByUsers
            .filter((user) => !user.isAutomation)
            .map(({ value }) => value),
          includeAutomation:
            updatedFilters.invitedByUsers.findIndex((filter) => filter.isAutomation) >= 0,
        }

  const hireHistory = {
    status: updatedFilters.hireHistory?.value ?? null,
    accounts: props.forOrganisation
      ? props.unfilteredDivisions.map((value) => value)
      : [currentAccount.globalId],
  }

  const hireStatus = {
    status: updatedFilters.hireStatus?.value ?? null,
    accounts: props.forOrganisation
      ? props.unfilteredDivisions.map((value) => value)
      : [currentAccount.globalId],
  }

  emit('update-filters', {
    statuses: updatedFilters.statuses.map(({ value }) => value),
    countries: updatedFilters.countries.map(({ value }) => value),
    states,
    invitedByUsers,
    origin: updatedFilters.origin?.value ?? null,
    skills: updatedFilters.skills.map(({ value }) => value),
    hireHistory,
    hireStatus,
    // Accounts is special since it can be updated both on tab change and on a filter
    // If the filter is reset to an empty array on the organisation tab, that really means that filtering
    // should be reset to use all divisions (accounts) other than the authenticated user's company
    accounts:
      props.forOrganisation && updatedFilters.accounts.length === 0
        ? [...props.divisions]
        : updatedFilters.accounts.map(({ value }) => value),
    businessSetup: updatedFilters.businessSetup.map(({ value }) => value),
    staffingAgencyStatus: updatedFilters.staffingAgencyStatus?.value ?? null,
    customFields: customFieldsGqlInput,
  })
})

const toggleFilters = () => {
  filtersAreOpened.value = !filtersAreOpened.value
  emit('filter-drawer-toggled', filtersAreOpened)
}

watch(
  () => props.workerPanelActive,
  (workerPanelActive) => {
    if (workerPanelActive && filtersAreOpened) {
      filtersAreOpened.value = false
    }
  },
)

const displayResetFiltersButton = computed(() => appliedFiltersCount.value > 0)

const displayStatesFilter = computed(() =>
  filters.countries.some((country) => country.value === 'US'),
)

const defaultAccountFilter = computed(() =>
  props.forOrganisation
    ? props.divisions.map((division) => ({ value: division }))
    : [{ value: currentAccount.globalId }],
)

const selectedCompanies = ref([])

watch(selectedCompanies, (companySelected) => {
  if (props.forOrganisation) {
    filters.accounts = companySelected.length > 0 ? companySelected : []
  }
})

const resetFilters = () => {
  filters.statuses = []
  filters.skills = []
  filters.states = []
  filters.origin = null
  filters.hireHistory = null
  filters.hireStatus = null
  filters.invitedByUsers = []
  filters.countries = []
  filters.accounts = defaultAccountFilter.value
  filters.businessSetup = []
  filters.staffingAgencyStatus = null
  customFieldFilters.length = 0 // Easy way to empty the array and not break references

  selectedCompanies.value = []
}

resetFilters()

watch(
  () => props.forOrganisation,
  (forOrganisation) => {
    resetFilters()
    const accounts = forOrganisation ? props.divisions : [currentAccount.globalId]
    queryVariables.value.accounts = accounts
    skillsVariables.value.accounts = accounts
  },
)

watch(
  () => props.showWorkerSkills,
  (showWorkerSkills) => {
    if (!showWorkerSkills) {
      const validSkillValues = skillsOptions.value.map((skill) => skill.value)

      filters.skills = filters.skills.filter((skill) => validSkillValues.includes(skill.value))
    }
  },
)

function toggleShowWorkerSkills() {
  emit('update:showWorkerSkills', !props.showWorkerSkills)
}
</script>

<style scoped lang="scss">
:deep(.wds-input-label) {
  margin-bottom: 0 !important;
}

.trusted-contact-filter {
  padding: 0;
}

.wds-button-ghost.filters-active {
  @apply tw-bg-true-gray-300;
}

:deep(.wds-label:not(.wds-selected-label)) {
  width: 100% !important;
}
</style>
